/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { LockClosedIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useLanguage } from "../../contexts/LanguageContext";
import { HEALTH_SEGMENTS, segmentLabelKey } from "../dashboard/segments";
import { SegmentDot } from "./ui";
import { chipClass } from "./styles";

// Search, card chips and health-segment chips above the list. Segments are a
// Growth feature: below it the chips still show, each with a lock, and a
// click opens the plan dialog instead of filtering (no request is made).
export default function CustomerFilters({
  search,
  onSearchChange,
  onSearchCommit,
  passes,
  passId,
  onPassChange,
  segment,
  onSegmentChange,
  segmentsLocked,
  onLockedSegment,
  hasFilters,
  onClear,
}) {
  const { t } = useLanguage();
  const tooShort = search.trim().length > 0 && search.trim().length < 3;

  const cardOptions = [
    { id: "all", label: t("customers.filter.allPasses") },
    ...(passes || []).map((pass) => ({ id: String(pass.id), label: pass.name })),
  ];

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[14px] border border-gray-200 bg-white p-3">
      <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-1">
        <label className="flex min-h-[42px] items-center gap-2 rounded-[10px] border border-gray-300 px-3 text-gray-500 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
          <MagnifyingGlassIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="sr-only">{t("customers.list.searchLabel")}</span>
          <input
            type="search"
            name="search"
            autoComplete="off"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onBlur={onSearchCommit}
            placeholder={t("customers.list.searchPlaceholder")}
            aria-describedby={tooShort ? "customers-search-hint" : undefined}
            className="min-h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-500 focus:ring-0"
          />
        </label>
        {tooShort && (
          <span id="customers-search-hint" className="px-1 text-xs text-gray-600">
            {t("customers.list.searchHint")}
          </span>
        )}
      </div>

      <div role="group" aria-label={t("customers.table.pass")} className="flex flex-wrap gap-1.5">
        {cardOptions.map((option) => {
          const on = String(passId) === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              onClick={() => onPassChange(option.id)}
              className={chipClass(on)}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div
        role="group"
        aria-label={t("customers.filter.segment")}
        className="flex flex-wrap items-center gap-1.5"
      >
        {/* "Everyone" is no filter at all, so a locked store does not get it. */}
        {(segmentsLocked ? HEALTH_SEGMENTS : [null, ...HEALTH_SEGMENTS]).map((id) => {
          const on = !segmentsLocked && segment === id;
          const label = id ? t(segmentLabelKey(id)) : t("customers.filter.everyone");
          return (
            <button
              key={id || "everyone"}
              type="button"
              aria-pressed={on}
              onClick={() => (segmentsLocked ? onLockedSegment() : onSegmentChange(id))}
              className={`${chipClass(on)} ${segmentsLocked ? "text-gray-500" : ""}`}
            >
              {id && <SegmentDot segment={id} />}
              {label}
              {segmentsLocked && (
                <>
                  <LockClosedIcon className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
                  <span className="sr-only">{t("customers.filter.locked")}</span>
                </>
              )}
            </button>
          );
        })}
        {segmentsLocked && (
          <button
            type="button"
            onClick={onLockedSegment}
            className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 text-[13px] font-semibold text-indigo-800 hover:bg-indigo-100"
          >
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("customers.filter.segmentsLocked", { plan: t("pricing.tier.growth.name") })}
          </button>
        )}
      </div>

      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="ml-auto min-h-9 rounded-lg px-2 text-[13px] font-semibold text-indigo-700 hover:text-indigo-800"
        >
          {t("customers.filter.clear")}
        </button>
      )}
    </div>
  );
}
